/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import { prettyDuration } from '../pretty_duration';
import { EuiI18n } from '../../../i18n';
import { htmlIdGenerator } from '../../../../services';
import { EuiTitle } from '../../../title';
import { EuiLink } from '../../../link';
import { EuiHorizontalRule } from '../../../horizontal_rule';
var generateId = htmlIdGenerator();
export var EuiRecentlyUsed = ({
  applyTime,
  commonlyUsedRanges,
  dateFormat,
  recentlyUsedRanges = []
}) => {
  const legendId = generateId();

  if (recentlyUsedRanges.length === 0) {
    return null;
  }

  const links = recentlyUsedRanges.map(({
    start,
    end
  }) => {
    const applyRecentlyUsed = () => {
      applyTime({
        start,
        end
      });
    };

    return <li className="euiQuickSelectPopover__sectionItem" key={`${start}-${end}`}>
        <EuiLink onClick={applyRecentlyUsed}>
          {prettyDuration(start, end, commonlyUsedRanges, dateFormat)}
        </EuiLink>
      </li>;
  });
  return <fieldset>
      <EuiTitle size="xxxs">
        <legend id={legendId}>
          <EuiI18n token="euiRecentlyUsed.legend" default="Recently used date ranges" />
        </legend>
      </EuiTitle>
      <div className="euiQuickSelectPopover__section">
        <ul>{links}</ul>
      </div>
      <EuiHorizontalRule margin="s" />
    </fieldset>;
};
EuiRecentlyUsed.propTypes = {
  applyTime: PropTypes.func.isRequired,
  commonlyUsedRanges: PropTypes.arrayOf(PropTypes.shape({
    end: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired,
    label: PropTypes.string,
    start: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired
  }).isRequired).isRequired,
  dateFormat: PropTypes.string.isRequired,
  recentlyUsedRanges: PropTypes.arrayOf(PropTypes.shape({
    end: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired,
    label: PropTypes.string,
    start: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired
  }).isRequired)
};
EuiRecentlyUsed.displayName = 'EuiRecentlyUsed';

try {
  EuiRecentlyUsed.__docgenInfo = {
    description: '',
    displayName: 'EuiRecentlyUsed',
    methods: [],
    props: {
      applyTime: {
        defaultValue: null,
        description: '',
        name: 'applyTime',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/quick_select_popover/recently_used.tsx',
          name: 'EuiRecentlyUsedProps'
        },
        required: true,
        type: {
          name: 'ApplyTime'
        }
      },
      commonlyUsedRanges: {
        defaultValue: null,
        description: '',
        name: 'commonlyUsedRanges',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/quick_select_popover/recently_used.tsx',
          name: 'EuiRecentlyUsedProps'
        },
        required: true,
        type: {
          name: 'DurationRange[]'
        }
      },
      dateFormat: {
        defaultValue: null,
        description: '',
        name: 'dateFormat',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/quick_select_popover/recently_used.tsx',
          name: 'EuiRecentlyUsedProps'
        },
        required: true,
        type: {
          name: 'string'
        }
      },
      recentlyUsedRanges: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'recentlyUsedRanges',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/quick_select_popover/recently_used.tsx',
          name: 'EuiRecentlyUsedProps'
        },
        required: false,
        type: {
          name: 'DurationRange[]'
        }
      }
    },
    extendedInterfaces: ['EuiRecentlyUsedProps']
  };
} catch (e) {}